import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { CoreUIAttachmentCardMetadataOrder } from '../../../cards/attachment-card/core-ui-attachment-card.component';
import type { CoreUIMediaItem, CoreUIMediaType } from '../../../media/core-ui-media.types';

export type FileUploadCardCaptionMode = 'action' | 'metadata';

@Component({
    selector: 'core-ui-file-upload-card',
    standalone: false,
    templateUrl: './file-upload-card.component.html',
    styleUrls: ['./file-upload-card.component.scss']
})
export class FileUploadCardComponent {

    @Input() fileName: string | null = null;
    @Input() fileUrl: string | null = null;
    @Input() fileSize: number | null = null;
    @Input() mediaType: CoreUIMediaType = 'document';
    @Input() captionMode: FileUploadCardCaptionMode = 'action';
    @Input() metadataOrder: CoreUIAttachmentCardMetadataOrder = 'size-format';
    @Input() accept = '*';
    @Input() readOnly = false;
    @Input() disabled = false;
    @Input() error = false;
    @Input() attachedLabel = 'Archivo adjunto:';
    @Input() emptyActionText = '+ Agregar documento';
    @Input() readonlyCaption = 'Haz clic para visualizar el documento';
    @Input() editableCaption = 'Haz clic para cambiar el archivo';
    @Input() description = '';
    @Input() iconName = 'icon_document';
    @Input() iconColor = '#000';
    @Input() actionAlign: 'left' | 'right' = 'left';

    @Output() fileSelected = new EventEmitter<File>();
    @Output() fileRemoved = new EventEmitter<void>();
    @Output() fileOpened = new EventEmitter<void>();

    isDragging = false;

    get hasFile(): boolean {
        return !!this.fileName;
    }

    get canEdit(): boolean {
        return !this.readOnly && !this.disabled;
    }

    get attachmentItem(): CoreUIMediaItem | null {
        if (!this.fileName) return null;
        return {
            id: this.fileName,
            url: this.fileUrl ?? '',
            name: this.fileName,
            type: this.mediaType,
            size: this.fileSize,
            thumbnailUrl: this.mediaType === 'image' ? this.fileUrl : null,
        };
    }

    get attachmentCaption(): string | null {
        if (this.captionMode === 'metadata') return null;
        return this.readOnly ? this.readonlyCaption : this.editableCaption;
    }

    openFilePicker(input: HTMLInputElement): void {
        if (!this.canEdit) return;
        input.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (file) {
            this.fileSelected.emit(file);
        }

        input.value = '';
    }

    onFileCardClick(input: HTMLInputElement): void {
        if (this.readOnly) {
            this.fileOpened.emit();
            return;
        }

        this.openFilePicker(input);
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();

        if (!this.canEdit) return;

        this.resetDragState();

        const file = event.dataTransfer?.files?.[0];
        if (file) {
            this.fileSelected.emit(file);
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();

        if (!this.canEdit) return;

        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();

        const currentTarget = event.currentTarget as HTMLElement;
        const relatedTarget = event.relatedTarget as Node;

        if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget)) return;

        this.resetDragState();
    }

    onDragEnd(): void {
        this.resetDragState();
    }

    removeFile(): void {
        if (this.disabled) return;

        this.resetDragState();
        this.fileRemoved.emit();
    }

    private resetDragState(): void {
        this.isDragging = false;
    }

}
